import type { SimulateDonationInput, SimulateDonationResponse } from '@signalvote/shared';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '../config';

/**
 * The demo-only "simulate a donation" call (issue #319). Unlike the real donor charge helpers in
 * `publicDonationApi.ts` — which are unauthenticated and hit the Stripe money path — this posts to
 * the AUTHENTICATED, demo-gated `POST /api/finance/dev/simulate-donation`. It is driven by a
 * logged-in rep demoing their own demo campaign: `authService.authenticatedFetch` attaches the rep's
 * token, `X-Subsidiary`, and the selected campaign's `X-Campaign-Id`, and the backend 403s any
 * non-demo campaign. No Stripe key, no card, no charge — the write lands a Pending gift in the ledger
 * through the shared finance services.
 */
export type SimulateDemoDonationResult =
  | ({ status: 'ok' } & SimulateDonationResponse)
  // The campaign isn't a demo (or the rep isn't a member) — the backend's defense-in-depth gate.
  | { status: 'forbidden' }
  | { status: 'error' };

export async function simulateDemoDonation(
  fields: SimulateDonationInput,
): Promise<SimulateDemoDonationResult> {
  try {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}/api/finance/dev/simulate-donation`,
      { method: 'POST', body: JSON.stringify(fields) },
    );
    if (response.status === 403) return { status: 'forbidden' };
    if (!response.ok) return { status: 'error' };
    const json: SimulateDonationResponse = await response.json();
    return {
      status: 'ok',
      contributionId: json.contributionId,
      contactId: json.contactId,
      processorContributionId: json.processorContributionId,
    };
  } catch {
    return { status: 'error' };
  }
}
